import React, { useContext, useEffect, useState } from "react";

import { ImageOptionsExtended } from "@/common/Options/ImageOptionsExtended";
import {
  OptionEnum,
  OptionModeEnum,
  OptionTypeEnum,
} from "@/components/Option";
import { VotingContext } from "@/contexts";
import LocaleContext from "@/i18n";
import { Option, VotingSlide, VotingVote } from "@/service/Model";
import Markdown from "@/utils/markdown";

import { Description, Header, SubWrapper, Title, Wrapper } from "./styles";

export type VotingOptionsProps = {
  interactionId: string;
  slide: VotingSlide;
  name: string;
  description?: string;
  voteCount: number;
  votes: VotingVote[];
  timing: number;
  isEnded?: boolean;
  isSummary?: boolean;
  showResults?: boolean;
  showCheckmarks?: boolean;
  onVotingChange?: (votes: VotingVote[]) => void;
  onVotingEnd?: (votes: VotingVote[]) => void;
};

export const VotingOptions: React.FC<VotingOptionsProps> = (
  props: VotingOptionsProps
): React.JSX.Element => {
  const [userVotes, setUserVotes] = useState<VotingVote[]>(props.votes || []);
  const [lastSelectedOption, setLastSelectedOption] = useState<string>("");
  const localeContext = useContext(LocaleContext);
  const votingContext = useContext(VotingContext);

  const {
    name,
    description,
    isEnded,
    interactionId,
    voteCount,
    isSummary,
    onVotingChange,
    onVotingEnd,
    timing,
    slide,
    showResults,
    showCheckmarks,
  } = props;

  useEffect(() => {
    if (isSummary) {
      // Hint: It's a stateless component for isSummary mode - derived state from props
      setUserVotes(props.votes);
    }
  }, [props.votes]);

  const getVotingOptions = (): React.JSX.Element => {
    const slideCompleted = voteCount === userVotes.length;

    const isUserVote = (option: Option) =>
      userVotes.some((vote: VotingVote) => vote.optionId === option.id);

    const resolveOptionMode = (option: Option): OptionModeEnum => {
      if (isSummary) {
        return OptionModeEnum.results; // force no pointer for all options
      }
      return isUserVote(option) ? OptionModeEnum.voted : OptionModeEnum.normal;
    };

    const resolveOption = (option: Option): OptionEnum | undefined => {
      if (isSummary) {
        return OptionEnum.vote; // force show all options as selected
      }
      return isUserVote(option) ? OptionEnum.vote : undefined;
    };

    const resolveOverlayContent = (option: Option): string | undefined => {
      return !slideCompleted && lastSelectedOption === option.id
        ? localeContext.translation.votingOverlay(voteCount - userVotes.length)
        : undefined;
    };

    const resolveClickable = (option: Option): boolean => {
      return isUserVote(option) ? !isEnded : !slideCompleted;
    };

    const resolvePercent = (option: Option): number | undefined =>
      showResults ? votingContext.resolvePercent(slide, option.id) : undefined;

    const resolveCheckmark = (option: Option): boolean =>
      isSummary && showCheckmarks ? isUserVote(option) : false;

    return (
      <ImageOptionsExtended
        type={OptionTypeEnum.voting}
        interactionId={interactionId}
        slide={slide}
        onVote={handleVote}
        resolveOption={resolveOption}
        resolveOptionMode={resolveOptionMode}
        resolveClickable={resolveClickable}
        resolveOverlayContent={resolveOverlayContent}
        resolvePercent={resolvePercent}
        resolveCheckmark={resolveCheckmark}
        animationDelay={timing}
      />
    );
  };

  const handleVote = (vote: VotingVote, isVoted: boolean): void => {
    if (!voteCount || isSummary) {
      return;
    }

    const add = () => [...userVotes, vote];
    const remove = () =>
      userVotes.filter(
        (userVote: VotingVote) => userVote.optionId !== vote.optionId
      );

    let updatedUserVotes = isVoted ? remove() : add();
    // Double check - allow only the allowed number
    updatedUserVotes = updatedUserVotes.slice(-voteCount);

    setUserVotes(updatedUserVotes);
    setLastSelectedOption(vote.optionId);

    if (onVotingChange) {
      onVotingChange(updatedUserVotes);
    }
    if (voteCount === updatedUserVotes.length && onVotingEnd) {
      onVotingEnd(updatedUserVotes);
    }
  };

  return (
    <Wrapper>
      <SubWrapper>
        <Header>
          <Title>{name}</Title>
          {description && (
            <Description
              dangerouslySetInnerHTML={{
                __html: Markdown(description),
              }}
            />
          )}
        </Header>
        {getVotingOptions()}
      </SubWrapper>
    </Wrapper>
  );
};

export default VotingOptions;
